import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ReplaySubject} from 'rxjs';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {ActivatedRoute, Router} from '@angular/router';
import {FilterService} from '../../../../shared/services/filter.service';
import {AuthService} from '../../../../shared/services/auth.service';
import {ChronieMaintenanceWindowService} from '../../../../shared/services/chronie-maintenance-window.service';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {MaintenanceWindowInsuranceCompany, MaintenanceWindowListItem} from '../../../../shared/interface/chronie-maintenance-window';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';

@Component({
  selector: 'app-chronie-all-maintenance-window-list',
  templateUrl: './chronie-all-maintenance-window-list.component.html',
  styleUrls: ['./chronie-all-maintenance-window-list.component.scss']
})
export class ChronieAllMaintenanceWindowListComponent implements AfterViewInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any = {};
  items: MaintenanceWindowListItem[] = [];
  totalData = 0;
  insuranceCompanies: MaintenanceWindowInsuranceCompany[] = [];
  loading = true;
  displayedColumns: string[] = [
    'displayFrom',
    'title',
    'insuranceCompany',
    'startsAt',
    'purchaseBlock',
    'status',
    'actions',
  ];

  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private chronieMaintenanceWindowService: ChronieMaintenanceWindowService,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();

    this.chronieMaintenanceWindowService.maintenanceWindowListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((response: CountedAndPaginated<MaintenanceWindowListItem>) => {
          this.items = response.items;
          this.totalData = response.pagination.total;
          this.loading = false;
        })
      )
      .subscribe();

    this.parseFilters();
    this.loadInsuranceCompanies();
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: PageEvent) => {
        this.router.navigate([
          this.activatedRoute.snapshot.routeConfig.path,
          this.filterService.parseFiltersToQueryParams({
            page: event.pageIndex,
            pageSize: event.pageSize,
            ...this.filtersForm.value,
          })
        ]);
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((routerState: RouterStateUrl) => {
        this.parseFilters(routerState.params);
        this.paginator.pageIndex = Number(routerState.params.page || 0);
        this.paginator.pageSize = Number(routerState.params.pageSize || 20);
        this.loadListData();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      uuid: 'string',
      enabled: 'int',
      noticeEnabled: 'int',
      blockPurchases: 'int',
      insuranceCompany: 'int',
    }, filters);
  }

  filter(): void {
    this.router.navigate([
      this.activatedRoute.snapshot.routeConfig.path,
      this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value,
      })
    ]);
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([
      this.activatedRoute.snapshot.routeConfig.path,
      {page: 0, pageSize: this.paginator.pageSize}
    ]);
  }

  loadListData(): void {
    this.chronieMaintenanceWindowService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      {...this.initialFilters},
    );
  }

  private loadInsuranceCompanies(): void {
    this.chronieMaintenanceWindowService.getInsuranceCompanies()
      .pipe(first())
      .subscribe({
        next: companies => {
          this.insuranceCompanies = companies;
        },
        error: () => {
          this.insuranceCompanies = [];
        }
      });
  }

  getStatus(item: MaintenanceWindowListItem): {
    label: string;
    type: 'neutral' | 'positive' | 'disabled' | 'negative'
  } {
    if (!item.enabled) {
      return {
        label: 'Wyłączona',
        type: 'negative'
      };
    }

    const now = new Date();

    const displayFrom = new Date(item.displayFrom);
    const displayTo = item.displayTo ? new Date(item.displayTo) : null;

    const startsAt = new Date(item.startsAt);
    const endsAt = new Date(item.endsAt);

    const noticeActive =
      item.noticeEnabled
      && displayFrom <= now
      && (!displayTo || displayTo > now);

    const purchaseBlockActive =
      item.blockPurchases
      && startsAt <= now
      && endsAt > now;

    if (noticeActive || purchaseBlockActive) {
      return {
        label: 'Aktywna teraz',
        type: 'positive'
      };
    }

    const hasFutureNotice =
      item.noticeEnabled
      && displayFrom > now;

    const hasFutureBlock =
      item.blockPurchases
      && startsAt > now;

    if (hasFutureNotice || hasFutureBlock) {
      return {
        label: 'Zaplanowana',
        type: 'neutral'
      };
    }

    return {
      label: 'Zakończona',
      type: 'disabled'
    };
  }

}
